import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, TrendingUp, Euro, Wallet, Receipt } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const CATEGORIES = ["rent", "salaries", "fuel", "vehicles", "software", "marketing", "taxes", "office", "other"];
const CAT_LABEL = {
  rent: "Affitto", salaries: "Stipendi", fuel: "Carburante", vehicles: "Veicoli",
  software: "Software", marketing: "Marketing", taxes: "Tasse", office: "Ufficio", other: "Altro",
};

function Card({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon size={16} strokeWidth={1.5} /></span>
      </div>
      <div className="mt-2 font-display text-xl lg:text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

export function ExpensesPanel() {
  const { user } = useAuth();
  const isStaff = ["admin", "support"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [fin, setFin] = useState(null);
  const [form, setForm] = useState({ category: "rent", description: "", amount: "", date: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/expenses").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/dashboard/financial").then(({ data }) => setFin(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Importo non valido"); return; }
    setSaving(true);
    try {
      await api.post("/expenses", {
        category: form.category, description: form.description || null,
        amount: Number(form.amount), date: form.date || null,
      });
      toast.success("Spesa aggiunta");
      setForm({ category: "rent", description: "", amount: "", date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa spesa?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("✓"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const header = ["date", "category", "description", "amount", "currency"];
    const csv = [header.join(",")].concat(
      rows.map((r) => header.map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`).join(","))
    ).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "spese.csv"; a.click();
  };

  return (
    <div className="space-y-5" data-testid="expenses-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={Euro} label="Incassi clienti" value={fin ? money(fin.revenue) : "—"} accent="bg-emerald-50 text-emerald-700" testid="exp-revenue" />
        <Card icon={Wallet} label="Pagamenti partner" value={fin ? money(fin.partner_payouts_total) : "—"} accent="bg-rose-50 text-rose-700" testid="exp-payouts" />
        <Card icon={Receipt} label="Spese" value={fin ? money(fin.expenses_total) : "—"} accent="bg-amber-50 text-amber-700" testid="exp-total" />
        <Card icon={TrendingUp} label="Profitto netto" value={fin ? money(fin.net_profit) : "—"} accent="bg-blue-50 text-blue-700" testid="exp-net-profit" />
      </div>
      <p className="text-xs text-slate-400">Profitto netto = Incassi clienti − Pagamenti partner − Spese</p>

      {isStaff && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-end gap-3" data-testid="expense-form">
          <div className="space-y-1.5 w-40"><Label>Categoria</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[c]}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5 flex-1 min-w-[180px]"><Label>Descrizione</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="es. Stipendio giugno" data-testid="expense-description" /></div>
          <div className="space-y-1.5 w-32"><Label>Importo €</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" /></div>
          <div className="space-y-1.5 w-40"><Label>Data</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" /></div>
          <Button onClick={add} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="expense-add-btn"><Plus size={16} className="mr-1" /> Aggiungi</Button>
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="expenses-export"><FileDown size={14} className="mr-1.5" /> Esporta CSV</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="expenses-table">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="text-xs uppercase text-slate-500">Data</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">Categoria</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">Descrizione</TableHead>
            <TableHead className="text-xs uppercase text-slate-500 text-right">Importo</TableHead>
            {isStaff && <TableHead className="text-xs uppercase text-slate-500 text-right">Azioni</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={isStaff ? 5 : 4} className="text-center py-10 text-slate-400">Nessuna spesa registrata</TableCell></TableRow>
              : rows.map((e) => (
                <TableRow key={e.id} data-testid="expense-row">
                  <TableCell className="text-sm text-slate-500">{formatDate(e.date)}</TableCell>
                  <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{CAT_LABEL[e.category] || e.category}</span></TableCell>
                  <TableCell className="text-sm text-slate-700">{e.description || "—"}</TableCell>
                  <TableCell className="text-right font-medium text-rose-600">-{money(e.amount, e.currency)}</TableCell>
                  {isStaff && <TableCell className="text-right"><Button size="icon" variant="ghost" onClick={() => remove(e.id)} data-testid={`expense-delete-${e.id}`}><Trash2 size={15} className="text-rose-500" /></Button></TableCell>}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
